import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  checkPermissionFromAssignments,
  getPermissionsFromAssignments,
  getStylingRolesForLocation,
  matchesLineAccess,
  type PermissionLocation,
} from '@/lib/permissions';
import { useUserRoleAssignments } from '@/hooks/useUserRoleAssignments';

export const useSiteRoles = (lineId?: string) => {
  const { user, isSuperAdmin, currentTenantId, allLines } = useAuth();
  const { data: assignments = [], isLoading: loading } = useUserRoleAssignments();

  const lineContext = useMemo(() => {
    if (!lineId) {
      return undefined;
    }

    const line = allLines.find((item) => item.id === lineId);
    if (!line) {
      return undefined;
    }

    return {
      factoryId: line.factory_id,
      areaId: line.area_id,
      lineId: line.id,
    } satisfies PermissionLocation;
  }, [allLines, lineId]);

  const permissions = useMemo(() => getPermissionsFromAssignments(assignments), [assignments]);
  const roles = useMemo(() => getStylingRolesForLocation(assignments, lineContext), [assignments, lineContext]);
  const resolvedLineId = lineId || null;

  const hasPermission = (permission: string, location?: PermissionLocation): boolean => {
    if (isSuperAdmin) return true;
    return checkPermissionFromAssignments(assignments, permission, location ?? lineContext);
  };

  const hasRole = (role: string): boolean => {
    return isSuperAdmin || roles.includes(role as typeof roles[number]);
  };

  const hasSiteAccess = (checkSiteId: string): boolean => {
    if (isSuperAdmin) return true;

    const line = allLines.find((item) => item.id === checkSiteId);
    if (!line) return false;

    return matchesLineAccess(assignments, {
      factoryId: line.factory_id,
      areaId: line.area_id,
      lineId: line.id,
    });
  };

  const hasAnyRole = (roleList: string[]): boolean => {
    return isSuperAdmin || roles.some((role) => roleList.includes(role));
  };

  return {
    roles,
    assignments,
    permissions,
    isAdmin: isSuperAdmin,
    loading,
    resolvedLineId,
    isReady: !loading && (lineId ? resolvedLineId === lineId : true),
    hasRole,
    hasAnyRole,
    hasPermission,
    hasSiteAccess,
    getUserSites: () => allLines.filter((line) => hasSiteAccess(line.id)).map((line) => line.id),
    isOperator: hasRole('operator'),
    isQuality: hasRole('quality'),
    isSiteManager: hasRole('site_manager'),
    canManage: hasPermission('mod_styling.quality') || hasPermission('mod_styling.site_manager'),
    canManageUsers: hasPermission('core.roles.view'),
  };
};
